import { UserMessage } from "@common-models/user-message";
import { DBBoardsThunks, DBUserMessageThunks, userMessagesSelectByQuery } from "@common-reducers/DBServiceThunks";
import { setHighlightedContactId } from "@common-reducers/WhatsAppReducer";
import { getMessageFromInputField } from "@common-services/utils";
import { getRandomMessageColor } from "@common-views/calendar/message-color";
import { appConfig } from "@common/config/app.config";
import { accessFormPopupActions } from "@common/reducers/AccessFormPopupReducer";
import { addScheduledMessageButtonActions } from "@common/reducers/AddScheduledMessageButtonReducer";
import { getCampaignStuckMessages } from "@common/reducers/CampaignSelectors";
import { mainSliceActions } from "@common/reducers/MainReducer";
import { taskActions } from "@common/reducers/TasksReducer";
import { userMessageActions } from "@common/reducers/UserMessageReducer";
import { selectUser } from "@common/reducers/UserSelectors";
import { selectProfilePic } from "@common/reducers/WhatsAppSelectors";
import { isWaSocketsLimitReached } from "@common/reducers/backend-api/backend-api";
import { AnalyticsService } from "@common/services/analytics-service";
import { RRuleRecurrenceHandler } from "@common/services/utils/rrule-recurrence-handler";
import { AppThunkDispatch, CommonRootState } from "@common/types/common-root-state-type";
import { MessageState } from "@common/types/interface";
import { AddScheduledMessageButtonState } from "@extension/context/content/components/add-scheduled-message-button/add-scheduled-message-button";
import { ContentMessageServiceActions } from "@extension/context/content/services/contentMessageServiceActions";
import { clearChatInputThunk, subscribeCurrentBoardToSocket } from "@extension/context/content/thunks/WhatsappThunks";
import { createAsyncThunk } from "@reduxjs/toolkit";
import moment from "moment";
import randomstring from "randomstring";

const analyticsService = new AnalyticsService();
const handler = new RRuleRecurrenceHandler();

// Group related thunks together
export const scheduleMessage = createAsyncThunk<
    void,
    UserMessage,
    { state: CommonRootState; dispath: AppThunkDispatch }
>("scheduleMessage", async (_message, thunkApi) => {
    const state = thunkApi.getState()
    const replyData = state.WhatsAppReducer.isReply

    let poll
    if (_message.isPoll) {
        poll = {
            ..._message.poll,
            options: _message.poll.options
                .filter(o => o.text && o.text !== ''),
        }
    } else {
        poll = {
            question: '',
            options: [
                { id: Math.floor(Math.random() * 1000000000000000000000000), text: '', order: 0 },
                { id: Math.floor(Math.random() * 1000000000000000000000000), text: '', order: 1 },
            ],
            allowMultiple: false,
        }
    }

    const message = {
        ..._message,
        id: randomstring.generate({ length: 8, charset: "alphanumeric" }),
        dueDate: moment(_message.dueDate).startOf("minute").toISOString(),
        status: 'pending',
        state: MessageState.pending,
        replyData,
        poll,
        messageColor: _message.messageColor ? _message.messageColor : getRandomMessageColor(),
    } as UserMessage;

    if (message?.replyData) {
        setTimeout(() => {
            const iconElement = document.querySelectorAll("footer [data-icon='x']")[1];

            if (iconElement instanceof HTMLElement) {
                iconElement.click();
            }
        }, 0); // A delay of 0ms to defer the action until the next tick
    }

    const profilePic = await selectProfilePic(state, message.contactList?.[0]?.id);
    if (message._id) {
        thunkApi.dispatch<any>(DBUserMessageThunks.patch({
            entity: {
                ...message,
                contactList: [{
                    ...message.contactList?.[0],
                    profilePicUrl: profilePic
                }]
            }
        }));
        analyticsService.event("update_scheduled_message", {
            category: "Schedule",
            action: "update scheduled message",
        });
    } else {
        message.disableOptimisticUpdates = true;
        thunkApi.dispatch<any>(DBUserMessageThunks.create({
            ...message,
            contactList: [{
                ...message.contactList?.[0],
                profilePicUrl: profilePic
            }]
        }));
        analyticsService.event("schedule_message", {
            category: "Schedule",
            action: "schedule message",
        });
    }
});

// Recurring message handling
export const handleRecurringMessageEditThunk = createAsyncThunk<
    void,
    {
        currentMessage: UserMessage,
        originalMessage: UserMessage,
        draggedDate: Date,
        newDate: Date,
        scope: 'all' | 'this' | 'thisAndFollowing'
    },
    { state: CommonRootState }
>("userMessages/handleRecurringMessageEdit", async ({ currentMessage, originalMessage, draggedDate, newDate, scope }, { dispatch }) => {
    if (!originalMessage?.rruleset) {
        console.log('[EDIT_RECURRING] No original message or rruleset found');
        return;
    }

    try {
        const currentRRule = currentMessage.isRecurring ? currentMessage.rruleset : undefined
        const result = handler.handleRecurringEdit(
            originalMessage.rruleset,
            draggedDate,
            newDate,
            currentRRule,
            scope
        );

        if (result.originalAction === 'delete') {
            console.log('[EDIT_RECURRING] Deleting original message');
            dispatch(deleteUserMessage(originalMessage._id));
        } else if (result.originalAction === 'patch') {
            console.log('[EDIT_RECURRING] Patching original message with new rule:', result.originalNewRRule);
            const patchedMessage = result.originalNewRRule
                ? { ...originalMessage, rruleset: result.originalNewRRule, isRecurring: true, dueDate: result.originalDueDate }
                : { ...originalMessage, rruleset: undefined, isRecurring: false, dueDate: result.originalDueDate };
            dispatch(scheduleMessage(patchedMessage));
        }

        if (result.newAction === 'create') {
            console.log('[EDIT_RECURRING] Creating new message');
            const newMessage = {
                ...currentMessage,
                _id: undefined,
                dueDate: result.newDueDate,
                rruleset: result.newRRule,
                isRecurring: !!result.newRRule
            };
            dispatch(scheduleMessage(newMessage));
        }
    } catch (error) {
        console.error('[EDIT_RECURRING] Error handling edit:', error);
        throw error;
    }
});

type HandleClickScheduleMessageThunkParams = {
    isSchedulePollButton?: boolean;
}

export const handleClickScheduleMessageThunk = createAsyncThunk<
    void,
    HandleClickScheduleMessageThunkParams,
    { state: CommonRootState }
>("scheduleMessage", async (HandleClickScheduleMessageThunkParams: HandleClickScheduleMessageThunkParams, thunkApi) => {
    const state: CommonRootState = thunkApi.getState();
    const dispatch = thunkApi.dispatch;
    const { isSchedulePollButton } = HandleClickScheduleMessageThunkParams
    const user = selectUser(state)
    const _currentChat = state.WhatsAppReducer.currentChat;
    const currentChat = {
        ..._currentChat,
        participants: undefined,
    }

    const resetState: AddScheduledMessageButtonState = {
        showErrorDialog: false,
        open: false,
        message: {
            dueDate: undefined,
            message: "",
            isRecurring: false,
            state: MessageState.pending,
            cancelIfReceived: false,
        },
        isEmojiPicker: false,
    };

    if (!user) {

        analyticsService.event("login_before_schedule", {
            category: "Schedule",
            action: "login before schedule",
        });

        dispatch(accessFormPopupActions.setStatus('Login'));
        return;
    }

    dispatch(isWaSocketsLimitReached.initiate({})).unwrap().then((res) => {
        dispatch(addScheduledMessageButtonActions.setIsSocketsLimitReached(res));
    });

    const message = Object.assign({}, resetState.message);
    message.contactList = [
        {
            ...currentChat,
            id: currentChat?.id?._serialized,
            name: currentChat?.name ?? currentChat?.displayName,
        },
    ];
    if (isSchedulePollButton) {
        ContentMessageServiceActions.getPollData()
    } else {
        message.message = getMessageFromInputField();
    }

    message.dueDate = moment().toDate().toISOString();
    message.isTimeValid = true;

    analyticsService.event("open_schedule_dialog", {
        category: "Schedule",
        action: "open dialog",
    });

    dispatch(addScheduledMessageButtonActions.editMessage(message));

    dispatch(clearChatInputThunk());
});



export const toggleTasksThunk = createAsyncThunk<
    void,
    UserMessage,
    { state: CommonRootState }
>("scheduleMessage", async (_message, thunkApi) => {
    const state: CommonRootState = thunkApi.getState();
    const dispatch = thunkApi.dispatch;
    const user = selectUser(state)
    const isTasksPanelOpen = state.TasksReducer.isTasksPanelOpen

    const boardsLoaded = state.DBBoardsReducer.itemsLoaded

    if (!user) {
        dispatch(accessFormPopupActions.setStatus('Login'))
        return;
    }

    console.log("ToggleTasksButton: run only once");
    if (!boardsLoaded) {
        console.log("get boards");
        dispatch(DBBoardsThunks.find({})).then(() => {
            dispatch(subscribeCurrentBoardToSocket())
        });
    }

    dispatch(taskActions.setIsTasksPanelOpen(!isTasksPanelOpen));
});



// CREATE THUNK getUserMessagesAndCampaigns
export const getPendingUserMessages = createAsyncThunk(
    "getUserMessages",
    async (_, { dispatch }) => {
        const userMessages = dispatch(
            DBUserMessageThunks.find({
                deleted: { $ne: true },
                status: "pending",
                $sort: {
                    dueDate: 1,
                },
                $limit: -1,
                "contactList.0._id": { $exists: true },
            })
        );
        return userMessages;
    }
);






// Thunk to send all stuck messages for a campaign
export const sendAllStuckMessages = createAsyncThunk(
    'campaign/sendAllStuckMessages',
    async (campaignId: string, { dispatch, getState }) => {
        const stuckMessages = getCampaignStuckMessages(getState(), campaignId);

        // update message dueDate to now
        for (const message of stuckMessages) {
            dispatch(DBUserMessageThunks.patch({
                entity: {
                    _id: message._id,
                    dueDate: moment().toDate().toISOString(),
                }
            }));
        }
    }
);

export const focusContactInCalendarThunk = createAsyncThunk(
    "focusContactInCalendar",
    async (contactId: string, { dispatch, getState }) => {
        const state = getState() as CommonRootState;
        const messages = userMessagesSelectByQuery(state.DBUserMessageReducer, {
            "contactList.0.id": contactId,
            status: "pending",
            $or: [{ deleted: { $exists: false } }, { deleted: false }]
        });

        if (messages.length > 0) {
            // Sort by date and get the earliest message
            const firstMessage = messages.sort((a, b) =>
                moment(a.dueDate).valueOf() - moment(b.dueDate).valueOf()
            )[0];

            // Open calendar view and navigate to the date of the first message
            dispatch(mainSliceActions.toggleCalendarView(true));
            dispatch(mainSliceActions.setViewMonthStart(moment(firstMessage.dueDate).toDate()));
        }

        // Highlight the contact's messages
        dispatch(setHighlightedContactId(contactId));

        // After 0.5 seconds, remove the highlight
        setTimeout(() => {
            dispatch(setHighlightedContactId(undefined));
        }, 500);
    }
);

export const getScheduledMessagesInRedisThunk = createAsyncThunk(
    'campaign/getScheduledMessagesInRedisThunk',
    async (campaignId: string, { rejectWithValue, dispatch, getState }) => {
        try {

            const state = getState() as any;
            const accessToken = state.UserReducer?.accessToken;

            if (!accessToken) {
                throw new Error('No access token available');
            }

            const response = await fetch(`${appConfig.API_ENDPOINT}/actions?cmd=get-scheduled-messages-in-redis`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: JSON.stringify({ campaignId }),
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch scheduled messages: ${response.status} ${response.statusText}`);
            }

            const data = await response?.json();
            if (!data || !data.messagesInRedis) {
                return [];
            }

            const messagesInRedis = data?.messagesInRedis.map((message) => ({
                id: message._id,
                contactName: message.text,
                createdAt: new Date(message.createdAt),
                status: "Waiting In Redis",
            }));
            const newObj = {
                [campaignId]: messagesInRedis
            }
            console.log('Payload:', newObj);

            dispatch(userMessageActions.setUserMessagesInRedisByCampaignId(newObj));

        } catch (error) {
            console.error('Error fetching scheduled messages:', error);
            return rejectWithValue(error.message || 'Unknown error');
        }
    }
);

export const deleteUserMessage = createAsyncThunk(
    'campaign/deleteUserMessage',
    async (userMessageId: string, { dispatch, getState }) => {
        dispatch(DBUserMessageThunks.patch({
            entity: {
                _id: userMessageId,
                deleted: true,
            },
            options: {
                inCaseOfErrorRollbackOptimisticUpdateDisabled: true,
            }
        }));
    }
);